import React, { useEffect, useState } from 'react'
import { Alert, QRCode } from 'antd'
import axios from 'axios'
import { copyTextToClipboard } from '../../App'
import CertificateType from '../../shared/types'
import s from './index.module.scss'
import { svgIcons } from '../../shared/utils/svg-icons'
import classNames from 'classnames'
import Button from '../../shared/ui/button'
import ExpandableText from '../../shared/ui/expandable-text'
import Certificate from '../../shared/ui/certificate'

interface Props {
    certificate: CertificateType
    giftGuid?: string
}

interface CertificateHistoryOperation {
    date: string
    type: string
    title: string
    amount: string
}

interface CertificateHistory {
    res_code: number
    res_msg?: string
    guid: string
    current_balance: string
    currency_symbol: string
    status: string
    updated_at: string
    operations: CertificateHistoryOperation[]
}

export default function MainPageActive({ certificate, giftGuid }: Props) {
    const [isOpenedInfo, setIsOpenedInfo] = useState(false)
    const [isOpenedHistory, setIsOpenedHistory] = useState(false)
    const [historyData, setHistoryData] = useState<CertificateHistory | null>(null)
    const [isHistoryLoading, setIsHistoryLoading] = useState(false)
    const [historyError, setHistoryError] = useState<string | null>(null)
    const certificateIsActive = certificate.request_status <= 1 && certificate.app_redeem != '1'

    const [showSuccessCopied, setShowSuccessCopied] = useState(false)
    useEffect(() => {
        let timeoutId: NodeJS.Timeout

        if (showSuccessCopied) {
            timeoutId = setTimeout(() => setShowSuccessCopied(false), 2000)
        }

        return () => clearTimeout(timeoutId)
    }, [showSuccessCopied])

    const loadHistory = async () => {
        if (!giftGuid) {
            setHistoryError('Guid not found.')
            return
        }

        setIsHistoryLoading(true)
        setHistoryError(null)

        try {
            const formData = new FormData()
            formData.append('guid', giftGuid)

            const result = await axios({
                method: 'post',
                url: 'https://app.giftyon.com/get_cert_history.php',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            })

            if (result.data?.res_code !== 0) {
                setHistoryError(result.data?.res_msg || 'Failed to load certificate history.')
                setHistoryData(null)
                return
            }

            setHistoryData(result.data)
        } catch (e) {
            setHistoryError('Failed to load certificate history.')
            setHistoryData(null)
        } finally {
            setIsHistoryLoading(false)
        }
    }

    const handleHistoryToggle = () => {
        const shouldOpen = !isOpenedHistory
        setIsOpenedHistory(shouldOpen)

        if (shouldOpen) {
            loadHistory()
        }
    }

    const nominalAmount = Number(certificate.nominal)
    const shouldShowQuantityAndAmount =
        Number(certificate.nominal_flexible) === 0 && isFinite(nominalAmount) && nominalAmount !== 0
    const shouldShowQuantityOnly = Number(certificate.nominal_flexible) === 0 && !shouldShowQuantityAndAmount
    const shouldShowHistory =
        certificate.show_history === true || certificate.show_history === 1 || certificate.show_history === '1'

    const formatNumberWithSpaces = (value: string | number) => {
        const valueString = String(value)
        const sign = valueString.startsWith('-') || valueString.startsWith('+') ? valueString[0] : ''
        const unsignedValue = sign ? valueString.slice(1) : valueString
        const parts = unsignedValue.split('.')

        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

        return `${sign}${parts.join('.')}`
    }

    const getMoneyAmount = (quantityValue: string) => {
        const targetQuantity = Number(quantityValue)

        if (!isFinite(nominalAmount) || !isFinite(targetQuantity)) {
            return quantityValue
        }

        const moneyAmount = targetQuantity * nominalAmount
        return moneyAmount > 0 && quantityValue.startsWith('+') ? `+${moneyAmount}` : String(moneyAmount)
    }

    return (
        <div className={s.container}>
            <h1>{certificate.greeting}</h1>

            <Certificate certificate={certificate} />

            {shouldShowHistory && (
                <div className={s.history}>
                    <button
                        type="button"
                        className={classNames(s.historyToggle, { [s.active]: isOpenedHistory })}
                        onClick={handleHistoryToggle}
                    >
                        <span>{certificate.balance_and_history}</span>
                        {svgIcons.arrow}
                    </button>

                    {isOpenedHistory && (
                        <div className={s.historyPanel}>
                            {isHistoryLoading && <p className={s.historyMessage}>Загрузка...</p>}

                            {!isHistoryLoading && historyError && <p className={s.historyError}>{historyError}</p>}

                            {!isHistoryLoading && !historyError && historyData && (
                                <>
                                    <div className={s.balanceSummary}>
                                        <div>
                                            <span>{certificate.current_balance}</span>
                                            {shouldShowQuantityAndAmount ? (
                                                <div className={s.balanceAmount}>
                                                    <b>
                                                        {formatNumberWithSpaces(historyData.current_balance)}{' '}
                                                        {certificate.quantity_symbol}
                                                    </b>
                                                    <b>
                                                        {formatNumberWithSpaces(
                                                            getMoneyAmount(historyData.current_balance),
                                                        )}{' '}
                                                        {historyData.currency_symbol}
                                                    </b>
                                                </div>
                                            ) : shouldShowQuantityOnly ? (
                                                <b>
                                                    {formatNumberWithSpaces(historyData.current_balance)}{' '}
                                                    {certificate.quantity_symbol}
                                                </b>
                                            ) : (
                                                <b>
                                                    {formatNumberWithSpaces(historyData.current_balance)}{' '}
                                                    {historyData.currency_symbol}
                                                </b>
                                            )}
                                        </div>
                                    </div>

                                    <div className={s.historyList}>
                                        <b>{certificate.transaction_history}</b>
                                        {historyData.operations.length > 0 ? (
                                            historyData.operations.map((operation, index) => (
                                                <div
                                                    className={s.historyItem}
                                                    key={`${operation.date}-${operation.type}-${index}`}
                                                >
                                                    <div className={s.operationInfo}>
                                                        <p>{operation.title}</p>
                                                        <span>{operation.date}</span>
                                                    </div>
                                                    {shouldShowQuantityAndAmount ? (
                                                        <div className={s.operationAmount}>
                                                            <b>
                                                                {formatNumberWithSpaces(operation.amount)}{' '}
                                                                {certificate.quantity_symbol}
                                                            </b>
                                                            <b>
                                                                {formatNumberWithSpaces(
                                                                    getMoneyAmount(operation.amount),
                                                                )}{' '}
                                                                {historyData.currency_symbol}
                                                            </b>
                                                        </div>
                                                    ) : shouldShowQuantityOnly ? (
                                                        <b>
                                                            {formatNumberWithSpaces(operation.amount)}{' '}
                                                            {certificate.quantity_symbol}
                                                        </b>
                                                    ) : (
                                                        <b>
                                                            {formatNumberWithSpaces(operation.amount)}{' '}
                                                            {historyData.currency_symbol}
                                                        </b>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className={s.historyMessage}>Операций по сертификату пока нет.</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            <p>
                <b>{certificate.issuer_title}: </b> {certificate.issuer_name}
            </p>

            <ExpandableText title={certificate.description_title} text={certificate.description} />

            {certificate.message_to_recipient && (
                <p>
                    <b>{certificate.message_to_recipient_title}: </b> {certificate.message_to_recipient}
                </p>
            )}

            <div className={s.actions}>
                <div>
                    <Button
                        className={classNames(s.btn, { [s.active]: isOpenedInfo })}
                        onClick={() => setIsOpenedInfo((prev) => !prev)}
                    >
                        {certificate.text_button_2}
                        {svgIcons.arrow}
                    </Button>

                    {isOpenedInfo &&
                        (certificateIsActive ? (
                            <div className={s.info}>
                                <div className={s.qr}>
                                    <b>{certificate.qr_title}</b>
                                    <QRCode size={250} value={certificate.deeplink} />
                                </div>
                                <div className={s.link}>
                                    <b>{certificate.link_title}</b>
                                    <div className={s.input}>
                                        {certificate.deeplink}
                                        <button
                                            onClick={() => {
                                                copyTextToClipboard(certificate.deeplink)
                                                setShowSuccessCopied(true)
                                            }}
                                        >
                                            {svgIcons.copy}
                                        </button>
                                    </div>
                                    {showSuccessCopied && (
                                        <Alert className={s.alert} message="Link copied" type="success" />
                                    )}
                                </div>
                                <p>{certificate.text_1}</p>
                                <br />
                                <p>{certificate.text_2}</p>
                            </div>
                        ) : (
                            <p className={s.info}>
                                {certificate.app_redeem == '1' ? certificate.text_5 : certificate.text_4}
                            </p>
                        ))}
                </div>

                {certificate.request_status <= 1 && (
                    <>
                        <Button
                            className={s.btn}
                            variant="danger"
                            onClick={() => window.open(certificate?.deeplink_2, '_blank')}
                        >
                            {certificate.text_button}
                        </Button>
                        <p className={s.ref}>{certificate.text_3}</p>
                    </>
                )}
            </div>
        </div>
    )
}
